import * as iotServicesSdk from "iot-services-sdk";
import axios from "axios";

const state = {
  rootCAs: [],
  currentCA: null,
  loading: false,
  error: null,
  baseUrl: null,
  authorizer: null,
  region: null,
  service: null,
};

const getters = {
  allRootCAs: (state) => state.rootCAs,
  rootCAById: (state) => (id) => state.rootCAs.find((ca) => ca.ca_id === id),
  selfManagedCAs: (state) =>
    state.rootCAs.filter((ca) => ca.ca_type === "self-managed"),
  awsManagedCAs: (state) =>
    state.rootCAs.filter((ca) => ca.ca_type === "aws-managed"),
  registeredCAs: (state) =>
    state.rootCAs.filter((ca) => ca.aws_registered === true),
  rootCACount: (state) => state.rootCAs.length,
};

const mutations = {
  init(state, {baseUrl, authorizer, region, service}) {
    state.baseUrl = baseUrl;
    state.authorizer = authorizer;
    state.region = region;
    state.service = service;
  },

  setRootCAs(state, rootCAs) {
    state.rootCAs = rootCAs;
  },

  addRootCA(state, rootCA) {
    state.rootCAs.push(rootCA);
  },

  updateRootCA(state, updatedCA) {
    const index = state.rootCAs.findIndex((ca) => ca.ca_id === updatedCA.ca_id);
    if (index !== -1) {
      state.rootCAs.splice(index, 1, updatedCA);
    }
  },

  removeRootCA(state, caId) {
    state.rootCAs = state.rootCAs.filter((ca) => ca.ca_id !== caId);
  },

  setCurrentCA(state, rootCA) {
    state.currentCA = rootCA;
  },

  setLoading(state, loading) {
    state.loading = loading;
  },

  setError(state, error) {
    state.error = error;
  },

  clearError(state) {
    state.error = null;
  },
};

const actions = {
  init({rootState, commit}) {
    const httpInfo = rootState.metadata.backendApi.http.prod;
    const baseUrl = "https://" + httpInfo.host + "/" + httpInfo.basePath;
    const service = httpInfo.service;
    const region = httpInfo.region;
    const authorizer = new iotServicesSdk.shared.auth.sigv4.SigV4Authorizer(
      service,
      region,
    );
    commit("init", {baseUrl, authorizer, region, service});
  },

  async fetchRootCAs({rootState, state, commit}) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/devices/root-cas", state.baseUrl);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "GET",
        url.hostname,
        url.pathname,
        url.search,
        "",
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.get(url.href, {headers: authHeaders});
      commit("setRootCAs", response.data);
      return response.data;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async getRootCA({rootState, state, commit}, caId) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/devices/root-cas/" + caId, state.baseUrl);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "GET",
        url.hostname,
        url.pathname,
        url.search,
        "",
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.get(url.href, {headers: authHeaders});
      commit("setCurrentCA", response.data);
      return response.data;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async getRegistrationCode({rootState, state}) {
    try {
      const url = new URL(
        "/v1/devices/root-cas/registration-code",
        state.baseUrl,
      );
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "GET",
        url.hostname,
        url.pathname,
        url.search,
        "",
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.get(url.href, {headers: authHeaders});
      return response.data.registration_code;
    } catch (error) {
      throw error;
    }
  },

  async createRootCA({rootState, state, commit}, caData) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/devices/root-cas", state.baseUrl);
      const body = JSON.stringify(caData);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "POST",
        url.hostname,
        url.pathname,
        url.search,
        body,
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.post(url.href, caData, {
        headers: authHeaders,
      });
      commit("addRootCA", response.data);
      return response.data;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async updateRootCA({rootState, state, commit}, {caId, caData}) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/devices/root-cas/" + caId, state.baseUrl);
      const body = JSON.stringify(caData);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "PUT",
        url.hostname,
        url.pathname,
        url.search,
        body,
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      const response = await axios.put(url.href, caData, {
        headers: authHeaders,
      });
      commit("updateRootCA", response.data || {ca_id: caId, ...caData});
      return response.data;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async deleteCA({rootState, state, commit}, caId) {
    commit("setLoading", true);
    commit("clearError");
    try {
      const url = new URL("/v1/devices/root-cas/" + caId, state.baseUrl);
      const sigv4Descriptor = new iotServicesSdk.shared.auth.sigv4.SigV4RequestDescriptor(
        rootState.authentication.credentials,
        "DELETE",
        url.hostname,
        url.pathname,
        url.search,
        "",
        state.service,
        state.region,
      );
      const authHeaders = state.authorizer.authorize(sigv4Descriptor);
      await axios.delete(url.href, {headers: authHeaders});
      commit("removeRootCA", caId);
      return true;
    } catch (error) {
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  clearError({commit}) {
    commit("clearError");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
